import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  GetAllBookingPaymentHistory,
  getAllPaymentMethods,
} from "adminApi/CustomersApi/CustomerRefund";

import DataTable from "Common/DataTable/DataTable";
import useDebouncedValue from "Common/DataTable/useDebouncedValue";
import useFetchAllData from "Hooks/useFetchAllData";

/* =========================================================
   PAYMENT TYPE STYLES
========================================================= */

const PAYMENT_TYPE_STYLES = {
  advance: {
    bg: "#f0f7ff",
    color: "#0d6efd",
    border: "#a8d0ff",
  },

  payment: {
    bg: "#f0fff4",
    color: "#198754",
    border: "#b2f2bb",
  },

  refund: {
    bg: "#fff5f5",
    color: "#dc3545",
    border: "#ffc9c9",
  },

  adjustment: {
    bg: "#fff9db",
    color: "#997404",
    border: "#ffe066",
  },
};

/* =========================================================
   MONEY FORMAT

   Display formatting only.
========================================================= */

const formatMoney = (value) => {
  return Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

/* =========================================================
   TAX ACCESSOR KEY
========================================================= */

const getTaxAccessorKey = (taxName) =>
  `tax_${String(taxName || "").replace(/\s+/g, "_")}`;

const getTaxLookupKey = (taxName) =>
  String(taxName || "")
    .trim()
    .toLowerCase();

/* =========================================================
   DEFAULT PAGE SUMMARY
========================================================= */

const DEFAULT_PAGE_SUMMARY = {
  totalAmount: 0,
  taxTotals: {},
  taxGrandTotal: 0,
  totalWithTax: 0,
};

/* =========================================================
   COMPONENT
========================================================= */

const BookingPaymentHistoryTable = ({
  filters = {},
  onSummaryChange,
  onTaxSummaryChange,
  onPaymentMethodsChange,
  canShowAction = false
}) => {
  /* =======================================================
     STATE
  ======================================================= */

  const [search, setSearch] = useState("");

  const [limit, setLimit] = useState(10);

  const [page, setPage] = useState(1);

  const [data, setData] = useState([]);

  const [taxColumns, setTaxColumns] = useState([]);

  const [pageSummary, setPageSummary] = useState(
    DEFAULT_PAGE_SUMMARY
  );

  const [meta, setMeta] = useState({
    page: 1,
    limit: 10,
    totalDocs: 0,
    totalPages: 0,
    prevPage: null,
    nextPage: null,
  });

  const [loading, setLoading] = useState(false);

  const [error, setError] = useState(null);

  const debouncedSearch = useDebouncedValue(search, 500);


  /* =======================================================
     PAYMENT METHODS API

     IMPORTANT:
     This is the ONLY source for payment method options.

     API response example:

     [
       {
         payment_method_id: 9,
         payment_method_name: "Bank Transfer"
       },
       {
         payment_method_id: 7,
         payment_method_name: "Cash"
       }
     ]
  ======================================================= */

  const {
    data: paymentMethodTypesOption,
    loading: loadingpaymentMethod,
  } = useFetchAllData(getAllPaymentMethods);

  /* =======================================================
     NORMALIZE PAYMENT METHOD OPTIONS

     Converts:

     {
       payment_method_id: 9,
       payment_method_name: "Bank Transfer"
     }

     INTO:

     {
       value: 9,
       label: "Bank Transfer"
     }
  ======================================================= */

  const paymentMethodOptions = useMemo(() => {
    const methods = Array.isArray(paymentMethodTypesOption)
      ? paymentMethodTypesOption
      : [];

    const options = methods
      .filter((method) => method?.payment_method_id != null)
      .map((method) => ({
        value: method.payment_method_id,
        label: method.payment_method_name || "",
      }));

    return [
      {
        value: "",
        label: "All Payment Methods",
      },
      ...options,
    ];
  }, [paymentMethodTypesOption]);

  /* =======================================================
     SEND PAYMENT METHOD OPTIONS TO PARENT

     Parent can now directly use:

     onPaymentMethodsChange(options)

     No dependency on payment-history API.
  ======================================================= */

  useEffect(() => {
    if (typeof onPaymentMethodsChange !== "function") {
      return;
    }

    onPaymentMethodsChange(paymentMethodOptions);
  }, [
    paymentMethodOptions,
    onPaymentMethodsChange,
  ]);

  /* =======================================================
     FETCH PAYMENT HISTORY
  ======================================================= */

  const fetchPaymentHistory = useCallback(
    async (requestPage = page) => {
      try {
        setLoading(true);
        setError(null);

        const requestFilters = {
          page: requestPage,
          limit,
          search: debouncedSearch,
          year: filters?.year || "",
          month: filters?.month || "",
          start_date: filters?.start_date || "",
          end_date: filters?.end_date || "",
          payment_method: filters?.payment_method || "",
          payment_type: filters?.payment_type || "",
          booking_status: filters?.booking_status || "",   // ← new
          sort: "payment_id",
          order: "DESC",
        };

        const result =
          await GetAllBookingPaymentHistory(
            requestFilters
          );

        if (!result?.success) {
          throw new Error(
            result?.message ||
            result?.error ||
            "Failed to fetch payment history"
          );
        }

        /* =================================================
           DATA
        ================================================= */

        const responseData = result?.data || [];

        setData(responseData);

        /* =================================================
           TAX COLUMNS
        ================================================= */

        setTaxColumns(result?.taxColumns || []);

        /* =================================================
           PAGE SUMMARY
        ================================================= */

        setPageSummary(
          result?.pageSummary ||
          DEFAULT_PAGE_SUMMARY
        );

        /* =================================================
           IMPORTANT

           DO NOT USE:

           result?.paymentMethods

           Payment methods are already loaded from:

           getAllPaymentMethods()

           above.
        ================================================= */

        /* =================================================
           META
        ================================================= */

        setMeta({
          page: Number(
            result?.page || requestPage
          ),

          limit: Number(
            result?.limit || limit
          ),

          totalDocs: Number(
            result?.totalDocs || 0
          ),

          totalPages: Number(
            result?.totalPages || 0
          ),

          prevPage:
            result?.prevPage ?? null,

          nextPage:
            result?.nextPage ?? null,
        });

        /* =================================================
           PAYMENT SUMMARY
        ================================================= */

        onSummaryChange?.({
          totalAdvance: Number(
            result?.totalAdvance || 0
          ),

          totalRefund: Number(
            result?.totalRefund || 0
          ),
        });

        /* =================================================
           TAX SUMMARY
        ================================================= */

        onTaxSummaryChange?.(
          result?.taxSummary || []
        );
      } catch (err) {

        setData([]);

        setTaxColumns([]);

        setPageSummary(
          DEFAULT_PAGE_SUMMARY
        );

        setMeta((prev) => ({
          ...prev,

          page: requestPage,

          totalDocs: 0,

          totalPages: 0,

          prevPage: null,

          nextPage: null,
        }));

        setError(
          err?.message ||
          "Failed to fetch booking payment history"
        );

        onSummaryChange?.({
          totalAdvance: 0,
          totalRefund: 0,
        });

        onTaxSummaryChange?.([]);

        /*
         * IMPORTANT:
         * Do NOT reset payment methods here.
         *
         * They come from getAllPaymentMethods()
         * and are independent from payment history.
         */
      } finally {
        setLoading(false);
      }
    },
    [
      page,
      limit,
      debouncedSearch,
      filters?.year,
      filters?.month,
      filters?.start_date,
      filters?.end_date,
      filters?.payment_method,
      filters?.payment_type,
      filters?.booking_status,
      onSummaryChange,
      onTaxSummaryChange,
    ]
  );

  /* =======================================================
     FILTER CHANGE
  ======================================================= */

  useEffect(() => {
    setPage(1);
  }, [
    filters?.year,
    filters?.month,
    filters?.booking_status,
    filters?.start_date,
    filters?.end_date,
    filters?.payment_method,
    filters?.payment_type,
  ]);

  /* =======================================================
     FETCH PAYMENT HISTORY
  ======================================================= */

  useEffect(() => {
    fetchPaymentHistory(page);
  }, [
    fetchPaymentHistory,
    page,
  ]);

  /* =======================================================
     BASE COLUMNS
  ======================================================= */

  const columns = [
    /* -----------------------------------------------------
       S.NO
    ----------------------------------------------------- */

    {
      Header: "#",

      accessor: "sno",

      disableSortBy: true,

      Cell: ({ row, value }) => {
        if (row.original?.isTotalRow) {
          return (
            <span
              className="fw-bold"
              style={{ fontSize: 14 }}
            >
              Total
            </span>
          );
        }

        return value;
      },
    },

    /* -----------------------------------------------------
       CUSTOMER
    ----------------------------------------------------- */

    {
      Header: "Customer",

      accessor: "first_name",

      Cell: ({ row }) => {
        if (row.original?.isTotalRow) {
          return "";
        }

        const name =
          `${row.original?.first_name || ""} ${row.original?.last_name || ""
            }`.trim();

        const contact =
          row.original?.email ||
          row.original?.customer_phone ||
          "—";

        return (
          <span
            style={{
              whiteSpace: "pre-line",
            }}
          >
            {`${name}\n${contact}`}
          </span>
        );
      },
    },

    /* -----------------------------------------------------
       BOOKING NUMBER
    ----------------------------------------------------- */

    {
      Header: "Booking Number",

      accessor: "booking_number",

      Cell: ({ row, value }) => {
        if (row.original?.isTotalRow) {
          return "";
        }

        return value || "—";
      },
    },

    /* -----------------------------------------------------
       AMOUNT
    ----------------------------------------------------- */

    {
      Header: "Amount",

      accessor: "amount",

      Cell: ({ row }) => {
        const isTotal =
          row.original?.isTotalRow;

        const amount = Number(
          row.original?.amount || 0
        );

        const paymentType = String(
          row.original?.payment_type || ""
        ).toLowerCase();

        const color = isTotal
          ? "#198754"
          : paymentType === "refund"
            ? "#dc3545"
            : paymentType === "advance"
              ? "#198754"
              : "inherit";

        return (
          <span
            className={
              isTotal
                ? "fw-bold"
                : "fw-semibold"
            }
            style={{
              color,
              whiteSpace: "nowrap",
            }}
          >
            ₹ {formatMoney(amount)}
          </span>
        );
      },
    },
  ];

  /* =======================================================
     DYNAMIC TAX COLUMNS
  ======================================================= */

  const dynamicTaxColumns = taxColumns.map(
    (tax) => {
      const taxName =
        tax?.tax_name || "";

      const taxRate = Number(
        tax?.rate || 0
      );

      const accessorKey =
        getTaxAccessorKey(taxName);

      return {
        Header: `${taxName} (${taxRate}%)`,

        accessor: accessorKey,

        disableSortBy: true,

        Cell: ({ row }) => {
          const isTotal =
            row.original?.isTotalRow;

          const taxAmount = Number(
            row.original?.[
            accessorKey
            ] || 0
          );

          return (
            <span
              className={
                isTotal
                  ? "fw-bold"
                  : "fw-semibold"
              }
              style={{
                whiteSpace: "nowrap",
              }}
            >
              ₹ {formatMoney(taxAmount)}
            </span>
          );
        },
      };
    }
  );

  /* =======================================================
     TOTAL AMOUNT COLUMN
  ======================================================= */

  const totalAmountColumn = {
    Header: "Total Amount",

    accessor: "total_amount",

    disableSortBy: true,

    Cell: ({ row }) => {
      const isTotal =
        row.original?.isTotalRow;

      const totalAmount = Number(
        row.original?.total_amount || 0
      );

      return (
        <span
          className={
            isTotal
              ? "fw-bold"
              : "fw-semibold"
          }
          style={{
            color: "#667eea",
            whiteSpace: "nowrap",
          }}
        >
          ₹ {formatMoney(totalAmount)}
        </span>
      );
    },
  };

  /* =======================================================
     REMAINING COLUMNS
  ======================================================= */

  columns.push(
    /* -----------------------------------------------------
       PAYMENT METHOD
    ----------------------------------------------------- */

    {
      Header: "Payment Method",

      accessor:
        "payment_method_name",

      Cell: ({ row, value }) => {
        if (row.original?.isTotalRow) {
          return "";
        }

        return value || "—";
      },
    },

    /* -----------------------------------------------------
       TRANSACTION ID
    ----------------------------------------------------- */

    {
      Header:
        "Payment Transaction ID",

      accessor:
        "transaction_id",

      Cell: ({ row, value }) => {
        if (row.original?.isTotalRow) {
          return "";
        }

        return value || "—";
      },
    },

    /* -----------------------------------------------------
       PAYMENT TYPE
    ----------------------------------------------------- */

    {
      Header: "Payment Type",

      accessor: "payment_type",

      Cell: ({ row, value }) => {
        if (row.original?.isTotalRow) {
          return "";
        }

        const type = String(
          value || ""
        ).toLowerCase();

        const style =
          PAYMENT_TYPE_STYLES[type] || {
            bg: "#f1f3f5",
            color: "#495057",
            border: "#dee2e6",
          };

        return (
          <span
            className="badge fw-semibold px-3 py-2"
            style={{
              background: style.bg,

              color: style.color,

              border: `1px solid ${style.border}`,

              borderRadius: 20,

              fontSize: 13,

              textTransform: "capitalize",
            }}
          >
            {value || "—"}
          </span>
        );
      },
    },

    /* -----------------------------------------------------
       PROCESSED BY
    ----------------------------------------------------- */

    {
      Header: "Processed By",

      accessor: "created_by",

      Cell: ({ row, value }) => {
        if (row.original?.isTotalRow) {
          return "";
        }

        return value || "—";
      },
    },

    {
      Header: "Processed At",

      accessor: "created_at",

      Cell: ({ row, value }) => {
        if (row.original?.isTotalRow) {
          return "";
        }

        return value || "—";
      },
    },

    /* -----------------------------------------------------
       DATE
    ----------------------------------------------------- */

    {
      Header: "Transaction Date",

      accessor: "transaction_date",

      Cell: ({ row, value }) => {
        if (row.original?.isTotalRow) {
          return "";
        }

        return value || "—";
      },
    },
  );

  /* =======================================================
     FINAL COLUMN ORDER
  ======================================================= */

  const finalColumns = [
    ...columns.slice(0, 4),

    ...dynamicTaxColumns,

    totalAmountColumn,

    ...columns.slice(4),
  ];

  /* =======================================================
     FORMAT DATA

     Flat tax fields are created only for export compatibility.
  ======================================================= */

  const formattedData = useMemo(() => {
    const rows = (data || []).map(
      (row, index) => {
        const flatRow = {
          ...row,

          sno:
            (page - 1) * limit +
            index +
            1,

          isTotalRow: false,
        };

        taxColumns.forEach((tax) => {
          const accessorKey =
            getTaxAccessorKey(
              tax?.tax_name
            );

          const lookupKey =
            getTaxLookupKey(
              tax?.tax_name
            );

          flatRow[accessorKey] =
            row?.taxByName?.[
            lookupKey
            ] || 0;
        });

        return flatRow;
      }
    );

    /* =====================================================
       TOTAL ROW
    ===================================================== */

    if (canShowAction && rows.length > 0) {
      const totalRow = {
        id: "payment-history-total-row",

        isTotalRow: true,

        sno: "Total",

        first_name: "",
        last_name: "",
        booking_number: "",

        amount: pageSummary?.totalAmount || 0,

        total_amount: pageSummary?.totalWithTax || 0,

        payment_method_name: "",
        transaction_id: "",
        payment_type: "",
        created_by: "",
        transaction_date: "",
      };

      taxColumns.forEach((tax) => {
        const accessorKey = getTaxAccessorKey(
          tax?.tax_name
        );

        const lookupKey = getTaxLookupKey(
          tax?.tax_name
        );

        totalRow[accessorKey] =
          pageSummary?.taxTotals?.[lookupKey] || 0;
      });

      rows.push(totalRow);
    }

    return rows;
  }, [
    data,
    page,
    limit,
    taxColumns,
    pageSummary,
    canShowAction
  ]);

  /* =======================================================
     SEARCH
  ======================================================= */

  const handleSearch = (value) => {
    setSearch(value);

    setPage(1);
  };

  /* =======================================================
     PAGINATION
  ======================================================= */

  const onPageChange = (newPage) => {
    setPage(newPage);
  };

  const onLimitChange = (newLimit) => {
    setLimit(newLimit);

    setPage(1);
  };

  /* =======================================================
     RENDER
  ======================================================= */

  const sizeOptions = [10, 20, 50, 100, 200, 500];

  return (
    <DataTable
      columns={finalColumns}

      data={formattedData}

      options={{
        loading,

        error,

        sortableColumns: [
          "first_name",
          "amount",
          "payment_type",
          "transaction_date",
          "transaction_id",
          "payment_method_name",
          "created_by",
          "created_at",
          "transaction_date",
          "booking_number",
          "total_amount",
        ],

        copy: true,

        csv: true,

        pdf: true,

        print: true,

        fileName:
          "BookingPaymentHistory",

        /* -----------------------------------------------
           Keep TOTAL row at bottom
        ----------------------------------------------- */

        sortType: (
          rowA,
          rowB
        ) => {
          if (
            rowA.original?.isTotalRow
          ) {
            return 1;
          }

          if (
            rowB.original?.isTotalRow
          ) {
            return -1;
          }

          return 0;
        },
      }}

      Search={{
        search: true,

        SearchInputValue: search,

        handleSearch,
      }}

      Entries={{
        Entries: true,

        limit,

        onLimitChange,

        options: sizeOptions,
      }}

      pagination={{
        isActive: true,

        meta,

        onPageChange,
      }}
    />
  );
};

export default BookingPaymentHistoryTable;